import { useCallback, useEffect, useState, useMemo } from "react";
import {
  addChild,
  moveChild,
  removeChild,
  safeAddToTree,
  updateComponent,
} from "./helpers";
import update from "immutability-helper";
import _ from "lodash";
import { rootNamespace } from "config";
import PubSub, { generateUID } from "lib/PubSub";
import api from "./api";

/**
 * structure of tree
    {
        stylesheets: [],
        onLoad: () => {
            // do something
        },
        scripts: [],
        body: {
            // tree
        }
    }
 */

function getFirstPage(pages) {
  if (pages) {
    return Object.keys(pages)[0];
  }
}

export default function useTree(treeData, id) {
  const pubSub = useMemo(() => new PubSub(), []);
  const [page, setPage] = useState(
    treeData ? getFirstPage(treeData.pages) : null
  );
  const [tree, setTree] = useState(treeData);
  const [selection, setSelection] = useState();
  const _setTree = useCallback(
    (tree) => {
      setTree(tree);
      if (id) {
        api("/api/projects/update", {
          id,
          data: JSON.stringify(tree),
        }).then((rsp) => {});
      }
    },
    [id]
  );
  const addScript = useCallback(
    (link) => {
      const scripts = tree.scripts || [];
      if (!scripts.includes(link))
        _setTree(safeAddToTree(tree, "scripts", link));
    },
    [tree, _setTree]
  );
  const addStylesheet = useCallback(
    (stylesheet) => {
      _setTree(safeAddToTree(tree, "stylesheets", stylesheet));
    },
    [tree, _setTree]
  );
  const removeStylesheet = useCallback(
    (stylesheet) => {
      const idx = tree.stylesheets.indexOf(stylesheet);
      _setTree(update(tree, _.set({}, "stylesheets.$splice", [[idx, 1]])));
    },
    [tree, _setTree]
  );
  // update entire tree
  const updateTree = useCallback(
    (path, data) => {
      _setTree(updateComponent(tree, path, data));
    },
    [tree, _setTree]
  );
  // update body/dom part of tree
  const updateBody = useCallback(
    (body) => {
      const path = _.set({}, ["pages", page, "body"].join("."), { $set: body });
      _setTree(update(tree, path));
    },
    [page, tree, _setTree]
  );
  useEffect(() => {
    return pubSub.subscribe(rootNamespace, "PROPS", (el) => {
      if (el.path === _.get(selection, "path")) setSelection();
      else setSelection(el);
    });
  }, [pubSub, selection]);
  useEffect(() => {
    // issue #6
    const body = _.get(tree, ["pages", page, "body"].join("."));
    const unregister = [
      pubSub.subscribe(rootNamespace, "ADD", ({ item, path }) =>
        updateBody(addChild(body, item, path))
      ),
      pubSub.subscribe(rootNamespace, "REMOVE", (path) => {
        updateBody(removeChild(body, path));
        setSelection();
      }),
      pubSub.subscribe(rootNamespace, "MOVE", ({ from, to }) => {
        updateBody(moveChild(body, from, to));
        if (selection && from === selection.path)
          setSelection({ ...selection, path: to });
      }),
      pubSub.subscribe(rootNamespace, "ADD_STYLESHEET", addStylesheet),
      pubSub.subscribe(rootNamespace, "REMOVE_STYLESHEET", removeStylesheet),
      pubSub.subscribe(rootNamespace, "ADD_SCRIPT", addScript),
    ];
    return () => unregister.forEach((callback) => callback());
  }, [
    pubSub,
    tree,
    selection,
    updateBody,
    addStylesheet,
    removeStylesheet,
    addScript,
    page,
  ]);
  useEffect(() => {
    pubSub.publish(rootNamespace, "SELECT", selection);
  }, [selection, pubSub]);
  // TODO: remove
  useEffect(() => {
    window.tree = tree;
  }, [tree]);
  function addPage(name) {
    const id = generateUID();
    _setTree({
      ...tree,
      pages: {
        ...tree.pages,
        [id]: { name },
      },
    });
  }
  function removePage(page) {
    const { [page]: toRemove, ...pages } = tree.pages;
    _setTree({
      ...tree,
      pages,
    });
  }
  const value = {
    tree: _.get(tree, "pages." + page, {}),
    updateTree,
    update: (path, data) =>
      updateTree(["pages", page, "body", path].join("."), data),
    addScript,
    addStylesheet,
    removeStylesheet,
    setSelection,
    publish: pubSub.publish,
    subscribe: pubSub.subscribe,
    page,
    setPage,
    addPage,
    removePage,
    pages: tree,
  };
  if (selection) {
    value.selection = {
      path: selection.path,
      ref: selection.ref,
      component: _.get(tree, ["pages", page, "body", selection.path].join(".")),
      style: selection.ref ? window.getComputedStyle(selection.ref) : null,
    };
  }
  return value;
}
